import { Card, Table, Tag, Progress } from "antd";
import { get } from "lodash";
import { useNavigate } from "react-router-dom";
import { BiTrendingUp, BiTrendingDown, BiEnvelope, BiGroup, BiTargetLock, BiLineChart, BiShieldQuarter, BiBarChartAlt2, BiRocket, BiMailSend } from "react-icons/bi";
import Header from "shared/components/Header";
import { useQuery } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import { SCREENS } from "utils/constants";

const STATE_COLORS = {
  draft: { dot: "#94A3B8", bg: "#F1F5F9", text: "#64748B", label: "Brouillon" },
  running: { dot: "#059669", bg: "#ECFDF5", text: "#065F46", label: "En cours" },
  paused: { dot: "#D97706", bg: "#FFFBEB", text: "#92400E", label: "En pause" },
  completed: { dot: "#2563EB", bg: "#EFF6FF", text: "#1E40AF", label: "Terminé" },
  archived: { dot: "#94A3B8", bg: "#F1F5F9", text: "#64748B", label: "Archivé" },
};

const KpiCard = ({ value, label, sub, color = "#2563EB", icon: Icon }) => (
  <div className="crm-kpi-card">
    {Icon && (
      <div className="crm-kpi-card__icon" style={{ background: `${color}12`, color }}>
        <Icon size={20} />
      </div>
    )}
    <div className="crm-kpi-card__body">
      <div className="crm-kpi-card__value" style={{ color }}>{value}</div>
      <div className="crm-kpi-card__label">{label}</div>
      {sub && <div className="crm-kpi-card__sub">{sub}</div>}
    </div>
  </div>
);

const BarMetric = ({ value, total, label, color = "#2563EB" }) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="crm-bar-metric">
      <div className="crm-bar-metric__row">
        <span className="crm-bar-metric__label">{label}</span>
        <span className="crm-bar-metric__numbers">
          <strong style={{ color }}>{value}</strong>
          <span className="crm-bar-metric__total"> / {total}</span>
        </span>
      </div>
      <div className="crm-bar-metric__track">
        <div className="crm-bar-metric__fill" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
};

const SectionHeader = ({ title, icon: Icon }) => (
  <div className="crm-section-header">
    {Icon && <Icon size={18} />}
    <h3>{title}</h3>
  </div>
);

const Dashboard = () => {
  const navigate = useNavigate();

  const { data: campaignsData } = useQuery(graphql.queries.CRM_CAMPAIGNS, { variables: {}, fetchPolicy: "network-only" });
  const { data: sendersData } = useQuery(graphql.queries.CRM_SENDERS, { variables: {} });
  const { data: leadsData } = useQuery(graphql.queries.CRM_LEADS, { variables: {}, fetchPolicy: "network-only" });
  const { data: campaignLeadsData } = useQuery(graphql.queries.CRM_CAMPAIGN_LEADS, { variables: {}, fetchPolicy: "network-only" });
  const { data: errorsData } = useQuery(graphql.queries.CRM_ERROR_LOGS, { variables: { take: 1000 }, fetchPolicy: "network-only" });

  const campaigns = get(campaignsData, "crmCampaigns.data") || [];
  const senders = get(sendersData, "crmSenders.data") || [];
  const leads = get(leadsData, "crmLeads.data") || [];
  const leadsCount = get(leadsData, "crmLeads.count", 0);
  const campaignLeads = get(campaignLeadsData, "crmCampaignLeads.data") || [];
  const errors = get(errorsData, "crmErrorLogs.data") || [];

  const running = campaigns.filter((c) => c.state === "running");
  const paused = campaigns.filter((c) => c.state === "paused");
  const completed = campaigns.filter((c) => c.state === "completed");
  const draft = campaigns.filter((c) => c.state === "draft");

  const activeSenders = senders.filter((s) => s.status === "active");
  const totalSentToday = senders.reduce((sum, s) => sum + (s.sentToday || 0), 0);
  const totalDailyCapacity = senders.reduce((sum, s) => sum + (s.dailyLimit || 0), 0);
  const avgBounceRate = senders.length > 0
    ? (senders.reduce((sum, s) => sum + (s.bounceRate || 0), 0) / senders.length).toFixed(1)
    : 0;
  const warmupSenders = senders.filter((s) => s.warmupStatus === "active");

  const clByStatus = campaignLeads.reduce((acc, cl) => {
    acc[cl.status] = (acc[cl.status] || 0) + 1;
    return acc;
  }, {});
  const inSequence = clByStatus.in_sequence || 0;
  const queued = clByStatus.queued || 0;
  const replied = clByStatus.replied || 0;
  const bounced = clByStatus.bounced || 0;
  const interested = clByStatus.interested || 0;
  const notInterested = clByStatus.not_interested || 0;
  const totalInCampaigns = campaignLeads.length;
  const conversionRate = totalInCampaigns > 0
    ? ((replied + interested) / totalInCampaigns * 100).toFixed(1)
    : "0.0";

  const leadsWithSante = leads.filter((l) => l.hasContratSante).length;
  const leadsWithPrevoyance = leads.filter((l) => l.hasContratPrevoyance).length;
  const leadsWithDeces = leads.filter((l) => l.hasContratDeces).length;
  const avgCotisation = leads.length > 0
    ? (leads.reduce((sum, l) => sum + (l.cotisationMensuelle || 0), 0) / leads.length).toFixed(0)
    : 0;

  const uniqueDepts = Object.keys(leads.reduce((acc, l) => {
    if (l.departement) acc[l.departement] = true;
    return acc;
  }, {})).length;

  const uniqueCompanies = Object.keys(leads.reduce((acc, l) => {
    if (l.companyName) acc[l.companyName] = true;
    return acc;
  }, {})).length;

  const multiEquip = leads.filter((l) =>
    [l.hasContratSante, l.hasContratPrevoyance, l.hasContratDeces].filter(Boolean).length >= 2
  ).length;
  const multiEquipRate = leads.length > 0 ? ((multiEquip / leads.length) * 100).toFixed(1) : "0.0";

  const stuckLeads = campaignLeads.filter((cl) => cl.stuckSince).length;

  return (
    <>
      <Header key="header" title="Tableau de bord CRM" />
      <div className="crm-dash">

        {/* Top KPI strip */}
        <div className="crm-dash__kpi-strip">
          <KpiCard value={campaigns.length} label="Campagnes" sub={`${running.length} actives`} color="#2563EB" icon={BiRocket} />
          <KpiCard value={leadsCount} label="Leads" sub={`${totalInCampaigns} en campagne`} color="#7C3AED" icon={BiGroup} />
          <KpiCard value={totalSentToday} label="Envois aujourd'hui" sub={`/ ${totalDailyCapacity} capacité`} color="#D97706" icon={BiMailSend} />
          <KpiCard value={`${conversionRate}%`} label="Transformation" sub={`${replied + interested} réponses`} color="#059669" icon={BiTrendingUp} />
          <KpiCard value={errors.length} label="Erreurs" sub={stuckLeads > 0 ? `${stuckLeads} bloqués` : "Aucun bloqué"} color="#DC2626" icon={BiTrendingDown} />
        </div>

        {/* Row 1: Activity + Contracts */}
        <div className="crm-dash__grid">
          <div className="crm-dash__card">
            <SectionHeader title="Activité commerciale" icon={BiLineChart} />
            <div className="crm-dash__metrics-row">
              <div className="crm-dash__metric-box">
                <span className="crm-dash__metric-num" style={{ color: "#94A3B8" }}>{queued}</span>
                <span className="crm-dash__metric-lbl">File d'attente</span>
              </div>
              <div className="crm-dash__metric-box">
                <span className="crm-dash__metric-num" style={{ color: "#2563EB" }}>{inSequence}</span>
                <span className="crm-dash__metric-lbl">En séquence</span>
              </div>
              <div className="crm-dash__metric-box">
                <span className="crm-dash__metric-num" style={{ color: "#059669" }}>{replied}</span>
                <span className="crm-dash__metric-lbl">Répondu</span>
              </div>
              <div className="crm-dash__metric-box">
                <span className="crm-dash__metric-num" style={{ color: "#059669" }}>{interested}</span>
                <span className="crm-dash__metric-lbl">Intéressés</span>
              </div>
              <div className="crm-dash__metric-box">
                <span className="crm-dash__metric-num" style={{ color: "#DC2626" }}>{notInterested}</span>
                <span className="crm-dash__metric-lbl">Pas intéressés</span>
              </div>
            </div>
          </div>

          <div className="crm-dash__card">
            <SectionHeader title="Portefeuille contrats" icon={BiShieldQuarter} />
            <BarMetric value={leadsWithSante} total={leads.length} label="Contrat Santé" color="#2563EB" />
            <BarMetric value={leadsWithPrevoyance} total={leads.length} label="Contrat Prévoyance" color="#7C3AED" />
            <BarMetric value={leadsWithDeces} total={leads.length} label="Contrat Décès" color="#64748B" />
            <div className="crm-dash__metrics-row" style={{ marginTop: 14 }}>
              <div className="crm-dash__metric-box">
                <span className="crm-dash__metric-num" style={{ color: "#059669" }}>{avgCotisation}€</span>
                <span className="crm-dash__metric-lbl">Cotis. moyenne</span>
              </div>
              <div className="crm-dash__metric-box">
                <span className="crm-dash__metric-num" style={{ color: "#D97706" }}>{multiEquipRate}%</span>
                <span className="crm-dash__metric-lbl">Multi-équipement</span>
              </div>
              <div className="crm-dash__metric-box">
                <span className="crm-dash__metric-num" style={{ color: "#2563EB" }}>{uniqueCompanies}</span>
                <span className="crm-dash__metric-lbl">Entreprises</span>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2: Pipeline + Campaigns table */}
        <div className="crm-dash__grid" style={{ marginTop: 16 }}>
          <div className="crm-dash__card">
            <SectionHeader title="Pipeline" icon={BiBarChartAlt2} />
            <BarMetric value={running.length} total={campaigns.length} label="En cours" color="#059669" />
            <BarMetric value={paused.length} total={campaigns.length} label="En pause" color="#D97706" />
            <BarMetric value={completed.length} total={campaigns.length} label="Terminées" color="#2563EB" />
            <BarMetric value={draft.length} total={campaigns.length} label="Brouillons" color="#94A3B8" />
            <div style={{ marginTop: 12 }}>
              <BarMetric value={bounced} total={totalInCampaigns} label="Rebonds" color="#DC2626" />
            </div>
          </div>

          <div className="crm-dash__card crm-dash__card--table">
            <SectionHeader title="Campagnes récentes" icon={BiRocket} />
            <Table
              dataSource={campaigns.slice(0, 5)}
              rowKey="id"
              size="small"
              pagination={false}
              onRow={(record) => ({
                onClick: () => navigate(`${SCREENS.PRIVATE.ADMIN.CAMPAIGNS.DETAIL.path}/${record.id}`),
                style: { cursor: "pointer" },
              })}
              columns={[
                { title: "Nom", dataIndex: "name", render: (name) => <strong style={{ color: "#0F172A" }}>{name}</strong> },
                {
                  title: "État", dataIndex: "state", width: 110,
                  render: (state) => {
                    const s = STATE_COLORS[state] || STATE_COLORS.draft;
                    return (
                      <Tag style={{ background: s.bg, color: s.text, border: "none", borderRadius: 12, fontSize: 11 }}>
                        <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: s.dot, marginRight: 5 }} />
                        {s.label}
                      </Tag>
                    );
                  },
                },
                { title: "Leads", dataIndex: ["health", "totalLeads"], width: 60, render: (v) => v || "—" },
              ]}
            />
          </div>
        </div>

        {/* Row 3: Bottom strip — Senders + Geography */}
        <div className="crm-dash__grid crm-dash__grid--3col" style={{ marginTop: 16 }}>
          <div className="crm-dash__card">
            <SectionHeader title="Expéditeurs" icon={BiEnvelope} />
            <div className="crm-dash__metrics-row">
              <div className="crm-dash__metric-box">
                <span className="crm-dash__metric-num" style={{ color: "#059669" }}>{activeSenders.length}<span style={{ fontSize: 14, color: "#94A3B8" }}>/{senders.length}</span></span>
                <span className="crm-dash__metric-lbl">Actifs</span>
              </div>
              <div className="crm-dash__metric-box">
                <span className="crm-dash__metric-num" style={{ color: parseFloat(avgBounceRate) > 5 ? "#DC2626" : "#059669" }}>{avgBounceRate}%</span>
                <span className="crm-dash__metric-lbl">Taux rebond</span>
              </div>
              <div className="crm-dash__metric-box">
                <span className="crm-dash__metric-num" style={{ color: "#D97706" }}>{warmupSenders.length}</span>
                <span className="crm-dash__metric-lbl">Warm-up</span>
              </div>
            </div>
          </div>

          <div className="crm-dash__card">
            <SectionHeader title="Couverture géographique" icon={BiTargetLock} />
            <div className="crm-dash__metrics-row">
              <div className="crm-dash__metric-box">
                <span className="crm-dash__metric-num" style={{ color: "#2563EB" }}>{uniqueDepts}</span>
                <span className="crm-dash__metric-lbl">Départements</span>
              </div>
              <div className="crm-dash__metric-box">
                <span className="crm-dash__metric-num" style={{ color: "#7C3AED" }}>{leadsCount}</span>
                <span className="crm-dash__metric-lbl">Leads totaux</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
